import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import template from 'lodash.template';
import cn from 'classnames';

import { HYDRA_DOUBLE_PRODUCT_IN_CART } from 'constants/hydraTests';

import { groupCartItemsBySizePrediction } from 'helpers/CartUtils';
import { isAssigned, triggerAssignment } from 'actions/ab';
import {
  cartError,
  fetchCartItems,
  fetchCartPromos,
  navigateToCheckout,
  updateCartCount
} from 'actions/cart';
import { pageTypeChange } from 'actions/common';
import { pluralize } from 'helpers';
import { translateCartError } from 'apis/mafia';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { Loader } from 'components/Loader';
import EmptyCart from 'components/cart/EmptyCart';
import CartItem from 'components/cart/CartItem';
import CartFavorites from 'components/cart/CartFavorites';
import CartCheckout from 'components/cart/CartCheckout';
import ContinueShopping from 'components/cart/ContinueShopping';
import CartHeadBanner from 'components/cart/CartHeadBanner';
import CartRecos from 'components/cart/CartRecos';
import CartErrors from 'components/cart/CartErrors';
import css from 'styles/containers/cart.scss';

export class Cart extends Component {
  static defaultProps = {
    updateCartCount
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
    marketplace: PropTypes.object,
    testId: PropTypes.func
  };

  static fetchDataOnServer(store, location) {
    const { dispatch } = store;
    const { err } = parse(location?.search);
    dispatch(triggerAssignment(HYDRA_DOUBLE_PRODUCT_IN_CART));

    const promises = [
      dispatch(fetchCartItems({ firePixel: true }))
    ];

    if (err) {
      const msg = translateCartError({ statusCode: '400', id: err });
      promises.push(dispatch(cartError(msg)));
    }

    return Promise.all(promises);
  }

  componentDidMount() {
    const {
      fetchCartItems,
      fetchCartPromos,
      pageTypeChange,
      updateCartCount,
      triggerAssignment,
      cart: { isLoaded, cartObj, promos }
    } = this.props;

    triggerAssignment(HYDRA_DOUBLE_PRODUCT_IN_CART);

    const { marketplace } = this.context;
    const { cart: { showTopBanner } } = marketplace;

    pageTypeChange('cart');

    if (!!showTopBanner && !promos) {
      fetchCartPromos();
    }

    if (!isLoaded) {
      fetchCartItems({ firePixel: true });
    } else if (cartObj) {
      updateCartCount(cartObj);
    }
  }

  componentDidUpdate(prevProps) {
    const { cartId, fetchCartItems } = this.props;
    if (prevProps.cartId !== cartId) {
      fetchCartItems({ firePixel: true });
    }
  }

  onCheckingOut = e => {
    e.preventDefault();
    this.props.navigateToCheckout(this.context.router);
  }

  render() {
    const { cart: cartState, promoData, showFixedQuantity } = this.props;
    const { isLoaded, loading } = cartState;

    if (!isLoaded) {
      return <Loader />;
    }

    const {
      testId,
      marketplace
    } = this.context;

    const {
      cart: { cartName, legalVerbiage, allowAdjustQuantity, showFitRecommendations },
      checkout: { helpUrl: { salesTax } }
    } = marketplace;

    const { cartObj: cart } = cartState;
    const { activeItems, savedItems, activeItemTotalQuantity } = cart;

    const { hydraDoubleProductInCart } = this.props;

    const taxVerbiage = template(legalVerbiage)({ salesTax });

    if (!(activeItems.length || savedItems.length)) {
      const { isCustomer } = this.props;
      return (
        <EmptyCart isCustomer={isCustomer}>
          <CartFavorites />
        </EmptyCart>
      );
    }

    const itemCountVerbiage = `${activeItemTotalQuantity} ${pluralize('item', activeItemTotalQuantity)}`;
    const { itemsWithFitRecommended, itemsWithFitNotRecommended, itemsWithNoRecommendationAvailable } = groupCartItemsBySizePrediction(activeItems);
    return (
      <SiteAwareMetadata>
        <div className={css.container}>
          <ContinueShopping />
          <CartHeadBanner promoData={promoData} />

          <h1><span className={cn({ [css.hideCartCount] : !activeItemTotalQuantity })}>{itemCountVerbiage} in </span>My {cartName}</h1>

          <CartErrors />

          <div className={css.wrapper}>
            <div className={css.items}>

              <div className={css.heading}>
                <span>Item</span>
                {allowAdjustQuantity && <span>Quantity</span> }
                <span>Price</span>
              </div>
              {(showFitRecommendations && hydraDoubleProductInCart)
                ?
                <>
                    {Boolean(itemsWithFitNotRecommended.length) &&
                      <div className={css.fitNotRecommended}>
                        <p className={css.fitCallout}>These sizes might not fit you!</p>
                        <p>Our handy-dandy sizing feature suggests that these sizes might not be the best fit for you.</p>
                        {itemsWithFitNotRecommended.map(item =>
                          <CartItem
                            item={item}
                            key={item.cartItemId}
                            showFixedQuantity={showFixedQuantity}
                          />
                        )}
                      </div>
                    }
                    {itemsWithFitRecommended.map(item =>
                      <CartItem
                        item={item}
                        isRecommendedFit={true}
                        key={item.cartItemId}
                        showFixedQuantity={showFixedQuantity}
                      />
                    )}
                    {itemsWithNoRecommendationAvailable.map(item =>
                      <CartItem
                        item={item}
                        key={item.cartItemId}
                        showFixedQuantity={showFixedQuantity}
                      />
                    )}
                  </>
                :
                <>
                    {activeItems.map(item => {
                      const { cartItemId } = item;
                      return <CartItem
                        item={item}
                        key={cartItemId}
                        showFixedQuantity={showFixedQuantity} />;
                    })}
                  </>
              }
              {savedItems.map(item => {
                const { cartItemId } = item;
                return <CartItem
                  item={item}
                  isUnavailable={true}
                  key={cartItemId} />;
              }
              )}
            </div>

            <div className={css.side}>
              <CartCheckout
                loading={loading}
                cart={cart}
                taxVerbiage={taxVerbiage}
                itemCountVerbiage={itemCountVerbiage}
                onCheckingOut={this.onCheckingOut} />
              <p
                className={css.legal}
                data-test-id={testId('legal')}
                dangerouslySetInnerHTML={{ __html: taxVerbiage }} />
            </div>
          </div>

          <CartRecos />

          <CartFavorites />
        </div>
      </SiteAwareMetadata>
    );
  }
}

export const mapStateToProps = state => {
  const {
    cart,
    cookies
  } = state;

  const isCustomer = !!cookies['x-main'];
  const promoData = cart.promos?.slotData?.['sidebar-2'];

  return {
    cart,
    isCustomer,
    promoData,
    hydraDoubleProductInCart: isAssigned(HYDRA_DOUBLE_PRODUCT_IN_CART, 1, state)
  };
};

const mapDispatchToProps = {
  cartError,
  fetchCartItems,
  fetchCartPromos,
  navigateToCheckout,
  pageTypeChange,
  triggerAssignment
};

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
